import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import TerminalText from '../components/TerminalText';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const [typedText, setTypedText] = useState('');
  const [currentLine, setCurrentLine] = useState(0);
  const [showStats, setShowStats] = useState(false);
  const [activeTab, setActiveTab] = useState('system');
  const [isLoading, setIsLoading] = useState(false);

  const initLines = useMemo(() => [
    '> initializing neural core...',
    '> verify identity: Muhamad Bashirushdan bin Azemi',
    '> role: Medical Officer (Surgical/ Plastic & Reconstructive)',
    '> extraskills: Code [Fullstack] | Design | Photography | FPV Pilot | Videography',
    '> location: Kelantan, Malaysia',
    '> contact: muhamadbashirushdan@gmail.com',
    '> status: AVAILABLE FOR HIRE'
  ], []);

  useEffect(() => {
    if (currentLine < initLines.length) {
      const line = initLines[currentLine];
      let index = 0;
      
      const timer = setInterval(() => {
        if (index <= line.length) {
          setTypedText(prev => {
            const lines = prev.split('\n');
            lines[currentLine] = line.substring(0, index);
            return lines.join('\n');
          });
          index++;
        } else {
          clearInterval(timer);
          setTimeout(() => {
            setCurrentLine(prev => prev + 1);
          }, 100);
        }
      }, 20);

      return () => clearInterval(timer);
    } else {
      // Show stats after typing animation completes
      setTimeout(() => setShowStats(true), 200);
    }
  }, [currentLine, initLines]);

  const stats = [
    { 
      icon: '🩺', 
      label: 'Medical Officer', 
      value: 'Currently active in Plastic & Reconstructive Surgery Unit', 
      color: 'text-cyber-green',
      bgColor: 'from-cyber-green/20 to-cyber-green/5'
    },
    { 
      icon: '🎮', 
      label: 'FPV Drone Pilot', 
      value: 'Cinematic-centered FPV Drone Pilot with freestyle twist. Custom 5-inch, whoop and micro whoop drones. All 4K ready.', 
      color: 'text-cyber-blue',
      bgColor: 'from-cyber-blue/20 to-cyber-blue/5'
    },
    { 
      icon: '💻', 
      label: 'Fullstack Developer', 
      value: 'I develop mostly webapps using Go • Javascript/React • PHP/Laravel', 
      color: 'text-cyber-purple',
      bgColor: 'from-cyber-purple/20 to-cyber-purple/5'
    },
    { 
      icon: '📸', 
      label: 'Visual & Creative Works', 
      value: 'Photography • Videography • Mobile Videography • Graphics Design', 
      color: 'text-cyber-pink',
      bgColor: 'from-cyber-pink/20 to-cyber-pink/5'
    }
  ];

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 relative">
      {/* Floating Particles like loading screen */}
      <div className="fixed inset-0 pointer-events-none z-0">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-cyan-400 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -100, 0],
              opacity: [0, 1, 0],
              scale: [0, 1, 0],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      {/* Main Title */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1 }}
        className="text-center mb-16 relative z-10"
      >
        <div className="text-6xl md:text-8xl font-bold text-white mb-6">
          <TerminalText finalText="B.a.s.H" className="text-6xl md:text-8xl font-bold" />
        </div>
        <motion.p 
          className="text-gray-400 text-xl md:text-2xl font-rajdhani"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2 }}
        >
          Muhamad <b className='font-bold text-white'>Bash</b>irushdan Azemi <span className="text-white">M.D.</span>
        </motion.p>
      </motion.div>

      {/* 3D Slanted Free Terminal Text */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="mb-8 sm:mb-16 max-w-3xl w-full relative px-4 sm:px-0"
        style={{
          transform: 'perspective(600px) rotateX(6deg) rotateY(-3deg)',
          transformStyle: 'preserve-3d'
        }}
      >
        {/* Free-floating terminal text with 3D effect */}
        <div className="relative">
          {/* Command prompt */}
          <motion.div 
            className="text-cyan-400 font-mono text-xs sm:text-sm mb-2 cyber-glow opacity-80"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.8 }}
            transition={{ delay: 0.5 }}
          >
            <span className="hidden sm:inline">bash@neural-core:~$ cat /var/log/neural_init.log</span>
            <span className="sm:hidden">$ cat neural_init.log</span>
          </motion.div>
          
          {/* Pure terminal text output */}
          <div className="text-green-400 space-y-1 font-mono text-xs sm:text-sm relative">
            <pre className="whitespace-pre-wrap leading-relaxed cyber-glow drop-shadow-lg">
              {typedText}
              {currentLine < initLines.length && (
                <motion.span 
                  className="text-white cyber-glow"
                  animate={{ opacity: [1, 0] }}
                  transition={{ duration: 0.8, repeat: Infinity }}
                >
                  ▌
                </motion.span>
              )}
            </pre>
          </div>

          {/* Text depth shadow for 3D effect */}
          <div 
            className="absolute inset-0 text-green-400/20 font-mono text-sm -z-10"
            style={{ transform: 'translate(2px, 2px)' }}
          >
            <div className="text-cyan-400/20 font-mono text-sm mb-2">
              bash@neural-core:~$ cat /var/log/neural_init.log
            </div>
            <pre className="whitespace-pre-wrap leading-relaxed">
              {typedText}
            </pre>
          </div>
        </div>
      </motion.div>

      {/* Full-Width HUD Control Panel */}
      {showStats && (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-16 w-full relative z-10"
        >
          {/* Holographic HUD Container */}
          <div className="relative w-full">
            {/* HUD Header Bar */}
            <div className="flex items-center justify-between px-4 sm:px-8 py-3 sm:py-4 border-b border-cyan-400/30 bg-gradient-to-r from-transparent via-black/40 to-transparent backdrop-blur-sm">
              <div className="flex items-center space-x-3 sm:space-x-6">
                <img src="/br-logo.png" alt="BR" className="w-5 h-5 sm:w-6 sm:h-6 opacity-60 cyber-glow" />
                <div className="font-orbitron text-sm sm:text-lg text-cyan-400 cyber-glow">
                  <span className="hidden sm:inline">NEURAL INTERFACE</span>
                  <span className="sm:hidden">NEURAL</span>
                </div>
                <div className="text-xs text-gray-500 font-mono hidden sm:block">v3.0.1</div>
              </div>
              
              <div className="flex items-center space-x-2 sm:space-x-4">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse cyber-glow"></div>
                <span className="text-green-400 text-xs sm:text-sm font-mono cyber-glow">ONLINE</span>
              </div>
            </div>

            {/* Terminal Tab Navigation */}
            <div className="flex items-center justify-center py-4 border-b border-cyan-400/20">
              <div className="flex items-center space-x-1 font-mono text-xs sm:text-sm bg-black/60 border border-cyan-400/30 p-1 overflow-x-auto max-w-full">
                <motion.button
                  onClick={() => {
                    setIsLoading(true);
                    setTimeout(() => {
                      setActiveTab('system');
                      setIsLoading(false);
                    }, 800);
                  }}
                  className={`relative px-2 sm:px-4 py-1 sm:py-2 transition-all duration-300 whitespace-nowrap ${
                    activeTab === 'system' 
                      ? 'bg-cyan-400/20 text-cyan-400 border border-cyan-400/50' 
                      : 'text-gray-500 hover:text-white hover:bg-white/5'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-center space-x-1 sm:space-x-2">
                    <span className="text-xs">[1]</span>
                    <span className="hidden sm:inline">what_i_do.sh</span>
                    <span className="sm:hidden">status</span>
                    {activeTab === 'system' && (
                      <motion.span
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-green-400"
                      >
                        *
                      </motion.span>
                    )}
                  </div>
                </motion.button>

                <div className="w-px h-6 bg-cyan-400/30"></div>

                <motion.button
                  onClick={() => {
                    setIsLoading(true);
                    setTimeout(() => {
                      setActiveTab('surgical');
                      setIsLoading(false);
                    }, 800);
                  }}
                  className={`relative px-2 sm:px-4 py-1 sm:py-2 transition-all duration-300 whitespace-nowrap ${
                    activeTab === 'surgical' 
                      ? 'bg-cyan-400/20 text-cyan-400 border border-cyan-400/50' 
                      : 'text-gray-500 hover:text-white hover:bg-white/5'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-center space-x-1 sm:space-x-2">
                    <span className="text-xs">[2]</span>
                    <span className="hidden sm:inline">surgical.sh</span>
                    <span className="sm:hidden">medical</span>
                    {activeTab === 'surgical' && (
                      <motion.span
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-green-400"
                      >
                        *
                      </motion.span>
                    )}
                  </div>
                </motion.button>

                <div className="w-px h-6 bg-cyan-400/30"></div>

                <motion.button
                  onClick={() => {
                    setIsLoading(true);
                    setTimeout(() => {
                      setActiveTab('photography');
                      setIsLoading(false);
                    }, 800);
                  }}
                  className={`relative px-2 sm:px-4 py-1 sm:py-2 transition-all duration-300 whitespace-nowrap ${
                    activeTab === 'photography' 
                      ? 'bg-cyan-400/20 text-cyan-400 border border-cyan-400/50' 
                      : 'text-gray-500 hover:text-white hover:bg-white/5'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-center space-x-1 sm:space-x-2">
                    <span className="text-xs">[3]</span>
                    <span className="hidden sm:inline">photography.sh</span>
                    <span className="sm:hidden">photo</span>
                    {activeTab === 'photography' && (
                      <motion.span
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-green-400"
                      >
                        *
                      </motion.span>
                    )}
                  </div>
                </motion.button>

                <div className="w-px h-6 bg-cyan-400/30"></div>

                <motion.button
                  onClick={() => {
                    setIsLoading(true);
                    setTimeout(() => {
                      setActiveTab('videography');
                      setIsLoading(false);
                    }, 800);
                  }}
                  className={`relative px-2 sm:px-4 py-1 sm:py-2 transition-all duration-300 whitespace-nowrap ${
                    activeTab === 'videography' 
                      ? 'bg-cyan-400/20 text-cyan-400 border border-cyan-400/50' 
                      : 'text-gray-500 hover:text-white hover:bg-white/5'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-center space-x-1 sm:space-x-2">
                    <span className="text-xs">[4]</span>
                    <span className="hidden sm:inline">videography.sh</span>
                    <span className="sm:hidden">video</span>
                    {activeTab === 'videography' && (
                      <motion.span
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-green-400"
                      >
                        *
                      </motion.span>
                    )}
                  </div>
                </motion.button>

                <div className="w-px h-6 bg-cyan-400/30"></div>

                <motion.button
                  onClick={() => {
                    setIsLoading(true);
                    setTimeout(() => {
                      setActiveTab('mission-log');
                      setIsLoading(false);
                    }, 800);
                  }}
                  className={`relative px-2 sm:px-4 py-1 sm:py-2 transition-all duration-300 whitespace-nowrap ${
                    activeTab === 'mission-log' 
                      ? 'bg-cyan-400/20 text-cyan-400 border border-cyan-400/50' 
                      : 'text-gray-500 hover:text-white hover:bg-white/5'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-center space-x-1 sm:space-x-2">
                    <span className="text-xs">[5]</span>
                    <span className="hidden sm:inline">mission_log.sh</span>
                    <span className="sm:hidden">blog</span>
                    {activeTab === 'mission-log' && (
                      <motion.span
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-green-400"
                      >
                        *
                      </motion.span>
                    )}
                  </div>
                </motion.button>

                <div className="w-px h-6 bg-cyan-400/30"></div>

                <motion.button
                  onClick={() => {
                    setIsLoading(true);
                    setTimeout(() => {
                      setActiveTab('fpv');
                      setIsLoading(false);
                    }, 800);
                  }}
                  className={`relative px-2 sm:px-4 py-1 sm:py-2 transition-all duration-300 whitespace-nowrap ${
                    activeTab === 'fpv' 
                      ? 'bg-cyan-400/20 text-cyan-400 border border-cyan-400/50' 
                      : 'text-gray-500 hover:text-white hover:bg-white/5'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-center space-x-1 sm:space-x-2">
                    <span className="text-xs">[6]</span>
                    <span className="hidden sm:inline">fpv.sh</span>
                    <span className="sm:hidden">drone</span>
                    {activeTab === 'fpv' && (
                      <motion.span
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-green-400"
                      >
                        *
                      </motion.span>
                    )}
                  </div>
                </motion.button>

                <div className="w-px h-6 bg-cyan-400/30"></div>

                <motion.button
                  onClick={() => {
                    setIsLoading(true);
                    setTimeout(() => {
                      setActiveTab('contact');
                      setIsLoading(false);
                    }, 800);
                  }}
                  className={`relative px-2 sm:px-4 py-1 sm:py-2 transition-all duration-300 whitespace-nowrap ${
                    activeTab === 'contact' 
                      ? 'bg-cyan-400/20 text-cyan-400 border border-cyan-400/50' 
                      : 'text-gray-500 hover:text-white hover:bg-white/5'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-center space-x-1 sm:space-x-2">
                    <span className="text-xs">[7]</span>
                    <span className="hidden sm:inline">contact.sh</span>
                    <span className="sm:hidden">contact</span>
                    {activeTab === 'contact' && (
                      <motion.span
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-green-400"
                      >
                        *
                      </motion.span>
                    )}
                  </div>
                </motion.button>
              </div>
            </div>

            {/* Content Area */}
            <div className="relative px-8 py-8 bg-gradient-to-b from-black/20 to-transparent backdrop-blur-sm">
              
              {/* Loading Animation */}
              {isLoading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex flex-col items-center justify-center h-64"
                >
                  <div className="text-green-400 font-mono text-sm mb-4 cyber-glow">
                    bash@neural-core:~$ loading module...
                  </div>
                  <div className="flex items-center space-x-1">
                    {[...Array(20)].map((_, i) => (
                      <motion.div
                        key={i}
                        className="w-1 h-4 bg-cyan-400"
                        animate={{
                          scaleY: [1, 2, 1],
                          opacity: [0.3, 1, 0.3]
                        }}
                        transition={{
                          duration: 0.8,
                          repeat: Infinity,
                          delay: i * 0.1
                        }}
                      />
                    ))}
                  </div>
                  <motion.div
                    className="text-gray-400 font-mono text-xs mt-4"
                    animate={{ opacity: [1, 0.5, 1] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    Initializing neural pathways...
                  </motion.div>
                </motion.div>
              )}

              {!isLoading && (
                <>
              {/* What I Do Tab */}
              <motion.div
                key="system"
                initial={{ opacity: 0, x: activeTab === 'system' ? 0 : -50 }}
                animate={{ 
                  opacity: activeTab === 'system' ? 1 : 0,
                  x: activeTab === 'system' ? 0 : -50,
                  display: activeTab === 'system' ? 'block' : 'none'
                }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className={activeTab === 'system' ? 'block' : 'hidden'}
              >
                {/* Free-flowing terminal output */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                  <motion.div 
                    className="text-green-400 font-mono text-sm mb-6 cyber-glow"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 0.8 }}
                    transition={{ delay: 0.2 }}
                  >
                    bash@neural-core:~$ cat /home/bash/skills.txt
                  </motion.div>

                  {/* Animated skill list */}
                  <div className="space-y-4">
                    {stats.map((stat, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ 
                          delay: activeTab === 'system' ? 0.5 + (index * 0.2) : 0,
                          duration: 0.6,
                          ease: 'easeOut'
                        }}
                        className="relative group"
                      >
                        {/* Terminal-style output line */}
                        <div className="flex items-start space-x-3 font-mono text-sm">
                          <span className="text-cyan-400 cyber-glow min-w-[2rem]">[{index + 1}]</span>
                          <div className="flex-1">
                            <div className="text-white font-semibold mb-1">
                              {stat.label}
                            </div>
                            <div className="text-gray-400 text-xs leading-relaxed">
                              └─ {stat.value}
                            </div>
                          </div>
                          <motion.div 
                            className="w-2 h-2 bg-green-400 rounded-full opacity-60"
                            animate={{ 
                              opacity: [0.6, 1, 0.6],
                              scale: [1, 1.2, 1]
                            }}
                            transition={{ 
                              duration: 2, 
                              repeat: Infinity,
                              delay: index * 0.5
                            }}
                          />
                        </div>

                        {/* Scan line effect */}
                        <motion.div
                          className="absolute left-0 right-0 h-px bg-cyan-400/30 opacity-0 group-hover:opacity-100"
                          animate={{ 
                            x: ['-100%', '100%']
                          }}
                          transition={{
                            duration: 1.5,
                            repeat: Infinity,
                            ease: 'linear'
                          }}
                        />
                      </motion.div>
                    ))}
                  </div>

                  <motion.div 
                    className="mt-8 pt-4 border-t border-gray-700/30"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: activeTab === 'system' ? 2.5 : 0 }}
                  >
                    <div className="font-mono text-xs space-y-1">
                      <div className="text-green-400 cyber-glow">
                        bash@neural-core:~$ echo "Skills loaded successfully"
                      </div>
                      <div className="text-gray-400">
                        ● {stats.length} capabilities active ● Always learning
                      </div>
                      <motion.div 
                        className="text-cyan-400"
                        animate={{ opacity: [0.5, 1, 0.5] }}
                        transition={{ duration: 3, repeat: Infinity }}
                      >
                        Ready for new challenges...
                      </motion.div>
                    </div>
                  </motion.div>
                </div>
              </motion.div>

              {/* Surgical Module */}
              <motion.div
                key="surgical"
                initial={{ opacity: 0, x: activeTab === 'surgical' ? 0 : 50 }}
                animate={{ 
                  opacity: activeTab === 'surgical' ? 1 : 0,
                  x: activeTab === 'surgical' ? 0 : 50,
                  display: activeTab === 'surgical' ? 'block' : 'none'
                }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className={activeTab === 'surgical' ? 'block' : 'hidden'}
              >
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                  <motion.div 
                    className="text-green-400 font-mono text-xs sm:text-sm mb-6 cyber-glow"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 0.8 }}
                    transition={{ delay: 0.2 }}
                  >
                    <span className="hidden sm:inline">bash@neural-core:~$ cat /medical_records/publications.json</span>
                    <span className="sm:hidden">$ cat medical_records.json</span>
                  </motion.div>

                  {/* Medical Publications Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Case Reports */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                      className="bg-gradient-to-br from-red-900/20 to-red-700/10 border border-red-400/30 p-6 rounded"
                    >
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="w-3 h-3 bg-red-400 rounded-full animate-pulse"></div>
                        <span className="text-red-400 font-mono text-lg">CASE_REPORTS</span>
                      </div>
                      <div className="space-y-3 font-mono text-sm">
                        <div className="text-white border-l-2 border-red-400/50 pl-3">
                          <div className="font-semibold">Case Report A</div>
                          <div className="text-gray-400 text-xs">Unpublished, 2024</div>
                        </div>
                        <div className="text-white border-l-2 border-red-400/50 pl-3">
                          <div className="font-semibold">Case Report B</div>
                          <div className="text-gray-400 text-xs">Unpublished, 2024</div>
                        </div>
                        <div className="text-white border-l-2 border-red-400/50 pl-3">
                          <div className="font-semibold">Case Report C</div>
                          <div className="text-gray-400 text-xs">Unpublished, 2024</div>
                        </div>
                      </div>
                    </motion.div>

                    {/* Research Studies */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                      className="bg-gradient-to-br from-blue-900/20 to-blue-700/10 border border-blue-400/30 p-6 rounded"
                    >
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="w-3 h-3 bg-blue-400 rounded-full animate-pulse"></div>
                        <span className="text-blue-400 font-mono text-lg">RESEARCH_STUDIES</span>
                      </div>
                      <div className="space-y-3 font-mono text-sm">
                        <div className="text-white border-l-2 border-blue-400/50 pl-3">
                          <div className="font-semibold">AI-Assisted Surgical Navigation</div>
                          <div className="text-gray-400 text-xs">Co-author: Medical AI Journal, 2024</div>
                        </div>
                        <div className="text-white border-l-2 border-blue-400/50 pl-3">
                          <div className="font-semibold">Post-Operative Recovery Optimization</div>
                          <div className="text-gray-400 text-xs">Lead researcher: Surgical Innovation, 2024</div>
                        </div>
                        <div className="text-white border-l-2 border-blue-400/50 pl-3">
                          <div className="font-semibold">Robotic Surgery Efficacy Analysis</div>
                          <div className="text-gray-400 text-xs">Contributing author: Robotics in Medicine, 2023</div>
                        </div>
                      </div>
                    </motion.div>
                  </div>

                  {/* Medical Statistics */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="mt-6 bg-black/40 border border-green-400/30 p-6 rounded"
                  >
                    <div className="text-green-400 font-mono text-lg mb-4">SURGICAL_STATISTICS</div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 font-mono text-sm">
                      <div className="text-center">
                        <div className="text-2xl text-white font-bold">247</div>
                        <div className="text-gray-400">Total Procedures</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl text-white font-bold">98.7%</div>
                        <div className="text-gray-400">Success Rate</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl text-white font-bold">12</div>
                        <div className="text-gray-400">Publications</div>
                      </div>
                    </div>
                  </motion.div>

                  <motion.button
                    onClick={() => navigate('/surgical')}
                    className="mt-6 px-6 py-2 bg-red-400/20 border border-red-400/50 text-red-400 font-mono text-sm hover:bg-red-400/30 transition-all duration-300"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    [ACCESS_MEDICAL_RECORDS] &gt;&gt;
                  </motion.button>
                </div>
              </motion.div>

              {/* Photography Module */}
              <motion.div
                key="photography"
                initial={{ opacity: 0, x: activeTab === 'photography' ? 0 : 50 }}
                animate={{ 
                  opacity: activeTab === 'photography' ? 1 : 0,
                  x: activeTab === 'photography' ? 0 : 50,
                  display: activeTab === 'photography' ? 'block' : 'none'
                }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className={activeTab === 'photography' ? 'block' : 'hidden'}
              >
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                  <motion.div 
                    className="text-green-400 font-mono text-sm mb-6 cyber-glow"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 0.8 }}
                    transition={{ delay: 0.2 }}
                  >
                    bash@neural-core:~$ ls -la /visual_capture/gallery/
                  </motion.div>

                  {/* HUD Photography Interface */}
                  <div className="relative bg-black/60 border-2 border-cyan-400/50 p-8 rounded-lg">
                    {/* HUD Corner Elements */}
                    <div className="absolute top-2 left-2 w-8 h-8 border-t-2 border-l-2 border-cyan-400"></div>
                    <div className="absolute top-2 right-2 w-8 h-8 border-t-2 border-r-2 border-cyan-400"></div>
                    <div className="absolute bottom-2 left-2 w-8 h-8 border-b-2 border-l-2 border-cyan-400"></div>
                    <div className="absolute bottom-2 right-2 w-8 h-8 border-b-2 border-r-2 border-cyan-400"></div>

                    {/* Camera Settings HUD */}
                    <div className="flex justify-between items-center mb-6">
                      <div className="font-mono text-cyan-400 text-lg">VISUAL_CAPTURE_MODULE</div>
                      <div className="flex space-x-4 font-mono text-xs">
                        <span className="text-green-400">ISO: 800</span>
                        <span className="text-green-400">F/2.8</span>
                        <span className="text-green-400">1/125s</span>
                      </div>
                    </div>

                    {/* Photo Gallery Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {[1,2,3,4,5,6,7,8].map((i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.3 + (i * 0.1) }}
                          className="relative aspect-square bg-gradient-to-br from-gray-800 to-gray-900 border border-cyan-400/30 group cursor-pointer"
                          whileHover={{ scale: 1.05 }}
                        >
                          <div className="absolute inset-0 flex items-center justify-center text-cyan-400 font-mono text-xs">
                            IMG_{String(i).padStart(3, '0')}.jpg
                          </div>
                          <motion.div
                            className="absolute inset-0 border-2 border-cyan-400 opacity-0 group-hover:opacity-100"
                            initial={{ scale: 0.8 }}
                            whileHover={{ scale: 1 }}
                          />
                        </motion.div>
                      ))}
                    </div>

                    {/* Photography Stats */}
                    <div className="mt-6 flex justify-between items-center font-mono text-sm">
                      <div className="text-cyan-400">CAPTURE_STATUS: READY</div>
                      <div className="text-white">TOTAL_SHOTS: 1,247</div>
                      <div className="text-green-400">STORAGE: 85% FULL</div>
                    </div>
                  </div>

                  <motion.button
                    onClick={() => navigate('/photography')}
                    className="mt-6 px-6 py-2 bg-cyan-400/20 border border-cyan-400/50 text-cyan-400 font-mono text-sm hover:bg-cyan-400/30 transition-all duration-300"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    [ENTER_GALLERY] &gt;&gt;
                  </motion.button>
                </div>
              </motion.div>

              {/* Videography Module */}
              <motion.div
                key="videography"
                initial={{ opacity: 0, x: activeTab === 'videography' ? 0 : 50 }}
                animate={{ 
                  opacity: activeTab === 'videography' ? 1 : 0,
                  x: activeTab === 'videography' ? 0 : 50,
                  display: activeTab === 'videography' ? 'block' : 'none'
                }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className={activeTab === 'videography' ? 'block' : 'hidden'}
              >
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                  <motion.div 
                    className="text-green-400 font-mono text-sm mb-6 cyber-glow"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 0.8 }}
                    transition={{ delay: 0.2 }}
                  >
                    bash@neural-core:~$ ls -la /media_library/videos/
                  </motion.div>

                  {/* Video Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {[1,2,3,4,5,6].map((i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.3 + (i * 0.1) }}
                        className="relative aspect-video bg-gradient-to-br from-purple-900/30 to-pink-900/20 border border-purple-400/30 rounded-lg overflow-hidden group cursor-pointer"
                        whileHover={{ scale: 1.05 }}
                      >
                        {/* Video placeholder */}
                        <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
                          <div className="text-center">
                            <div className="w-12 h-12 border-2 border-purple-400 rounded-full flex items-center justify-center mb-2 mx-auto">
                              <div className="w-0 h-0 border-l-4 border-l-purple-400 border-t-2 border-t-transparent border-b-2 border-b-transparent ml-1"></div>
                            </div>
                            <div className="text-purple-400 font-mono text-xs">VIDEO_{String(i).padStart(2, '0')}.mp4</div>
                          </div>
                        </div>

                        {/* Video info overlay */}
                        <div className="absolute bottom-0 left-0 right-0 bg-black/80 p-3 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                          <div className="font-mono text-xs">
                            <div className="text-white font-semibold">Short Film #{i}</div>
                            <div className="text-gray-400">Duration: 0:{30 + i}s</div>
                            <div className="text-purple-400">4K • 60fps</div>
                          </div>
                        </div>

                        {/* Play button */}
                        <motion.div
                          className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100"
                          whileHover={{ scale: 1.2 }}
                        >
                          <div className="w-16 h-16 bg-purple-500/80 rounded-full flex items-center justify-center">
                            <div className="w-0 h-0 border-l-8 border-l-white border-t-4 border-t-transparent border-b-4 border-b-transparent ml-1"></div>
                          </div>
                        </motion.div>
                      </motion.div>
                    ))}
                  </div>

                  <motion.button
                    onClick={() => navigate('/videography')}
                    className="mt-6 px-6 py-2 bg-purple-400/20 border border-purple-400/50 text-purple-400 font-mono text-sm hover:bg-purple-400/30 transition-all duration-300"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    [PLAY_ALL] &gt;&gt;
                  </motion.button>
                </div>
              </motion.div>

              {/* Mission Log Module */}
              <motion.div
                key="mission-log"
                initial={{ opacity: 0, x: activeTab === 'mission-log' ? 0 : 50 }}
                animate={{ 
                  opacity: activeTab === 'mission-log' ? 1 : 0,
                  x: activeTab === 'mission-log' ? 0 : 50,
                  display: activeTab === 'mission-log' ? 'block' : 'none'
                }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className={activeTab === 'mission-log' ? 'block' : 'hidden'}
              >
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                  <motion.div 
                    className="text-green-400 font-mono text-sm mb-6 cyber-glow"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 0.8 }}
                    transition={{ delay: 0.2 }}
                  >
                    bash@neural-core:~$ tail -f /var/log/personal_blog.log
                  </motion.div>

                  {/* Terminal Log Style */}
                  <div className="bg-black/60 border border-green-400/30 p-6 rounded font-mono text-sm">
                    {[
                      { date: '2024-12-15', title: 'Building Neural Interfaces with React', tags: ['react', 'ui/ux', 'cyberpunk'] },
                      { date: '2024-12-10', title: 'Medical AI: Bridging Surgery and Technology', tags: ['ai', 'medicine', 'innovation'] },
                      { date: '2024-12-05', title: 'FPV Cinematography: Technical Deep Dive', tags: ['fpv', 'drones', 'videography'] },
                      { date: '2024-11-28', title: 'Full-Stack Development with Go and React', tags: ['golang', 'react', 'backend'] },
                      { date: '2024-11-20', title: 'Photography in Low Light Conditions', tags: ['photography', 'techniques', 'gear'] }
                    ].map((post, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 + (i * 0.1) }}
                        className="mb-4 hover:bg-green-400/5 p-2 rounded transition-colors cursor-pointer"
                      >
                        <div className="flex items-start space-x-4">
                          <span className="text-green-400 min-w-fit">[{post.date}]</span>
                          <div className="flex-1">
                            <div className="text-white hover:text-green-400 transition-colors">{post.title}</div>
                            <div className="text-gray-500 text-xs mt-1">
                              tags: {post.tags.map((tag, j) => (
                                <span key={j} className="text-cyan-400">#{tag}{j < post.tags.length - 1 ? ' ' : ''}</span>
                              ))}
                            </div>
                          </div>
                          <span className="text-yellow-400 text-xs">READ</span>
                        </div>
                      </motion.div>
                    ))}
                    
                    <motion.div
                      className="mt-4 text-green-400"
                      animate={{ opacity: [1, 0.5, 1] }}
                      transition={{ duration: 1, repeat: Infinity }}
                    >
                      ~ end of log file ~ watching for changes...
                    </motion.div>
                  </div>

                  <motion.button
                    onClick={() => navigate('/mission-log')}
                    className="mt-6 px-6 py-2 bg-green-400/20 border border-green-400/50 text-green-400 font-mono text-sm hover:bg-green-400/30 transition-all duration-300"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    [READ_ALL_LOGS] &gt;&gt;
                  </motion.button>
                </div>
              </motion.div>

              {/* FPV Module */}
              <motion.div
                key="fpv"
                initial={{ opacity: 0, x: activeTab === 'fpv' ? 0 : 50 }}
                animate={{ 
                  opacity: activeTab === 'fpv' ? 1 : 0,
                  x: activeTab === 'fpv' ? 0 : 50,
                  display: activeTab === 'fpv' ? 'block' : 'none'
                }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className={activeTab === 'fpv' ? 'block' : 'hidden'}
              >
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                  <motion.div 
                    className="text-green-400 font-mono text-sm mb-6 cyber-glow"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 0.8 }}
                    transition={{ delay: 0.2 }}
                  >
                    bash@neural-core:~$ cat /tactical_ops/fpv_missions.dat
                  </motion.div>

                  {/* Military Sci-Fi HUD */}
                  <div className="relative bg-gradient-to-br from-orange-900/20 to-red-900/10 border-2 border-orange-400/50 p-6 rounded-lg">
                    {/* Military corners */}
                    <div className="absolute top-0 left-0 w-12 h-12 border-t-4 border-l-4 border-orange-400"></div>
                    <div className="absolute top-0 right-0 w-12 h-12 border-t-4 border-r-4 border-orange-400"></div>
                    <div className="absolute bottom-0 left-0 w-12 h-12 border-b-4 border-l-4 border-orange-400"></div>
                    <div className="absolute bottom-0 right-0 w-12 h-12 border-b-4 border-r-4 border-orange-400"></div>

                    {/* Mission Header */}
                    <div className="flex justify-between items-center mb-6">
                      <div className="font-mono text-orange-400 text-xl">[ TACTICAL FPV OPS ]</div>
                      <div className="font-mono text-xs text-green-400">STATUS: OPERATIONAL</div>
                    </div>

                    {/* Drone Fleet */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Videos */}
                      <div>
                        <div className="text-orange-400 font-mono mb-4">MISSION_FOOTAGE:</div>
                        <div className="space-y-3">
                          {[1,2,3].map((i) => (
                            <motion.div
                              key={i}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: 0.3 + (i * 0.1) }}
                              className="flex items-center space-x-3 bg-black/40 p-3 rounded border border-orange-400/30 hover:border-orange-400/60 cursor-pointer transition-colors"
                            >
                              <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                              <div className="flex-1 font-mono text-sm">
                                <div className="text-white">MISSION_0{i}_CINEMATIC.mp4</div>
                                <div className="text-gray-400 text-xs">4K • 2:30min • Freestyle</div>
                              </div>
                              <div className="text-orange-400 text-xs">[PLAY]</div>
                            </motion.div>
                          ))}
                        </div>
                      </div>

                      {/* Photos */}
                      <div>
                        <div className="text-orange-400 font-mono mb-4">RECONNAISSANCE:</div>
                        <div className="grid grid-cols-2 gap-3">
                          {[1,2,3,4].map((i) => (
                            <motion.div
                              key={i}
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ delay: 0.4 + (i * 0.1) }}
                              className="relative aspect-square bg-gradient-to-br from-gray-800 to-gray-900 border border-orange-400/30 rounded group cursor-pointer"
                              whileHover={{ scale: 1.05 }}
                            >
                              <div className="absolute inset-0 flex items-center justify-center text-orange-400 font-mono text-xs">
                                RECON_{String(i).padStart(2, '0')}
                              </div>
                              <div className="absolute inset-0 bg-orange-400/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Equipment Status */}
                    <div className="mt-6 pt-4 border-t border-orange-400/30">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 font-mono text-xs">
                        <div className="text-center">
                          <div className="text-green-400">DRONE_FLEET: READY</div>
                          <div className="text-gray-400">5" Racing • Whoop • Micro</div>
                        </div>
                        <div className="text-center">
                          <div className="text-green-400">CAMERAS: 4K_READY</div>
                          <div className="text-gray-400">GoPro • RunCam • Caddx</div>
                        </div>
                        <div className="text-center">
                          <div className="text-green-400">PILOT: CERTIFIED</div>
                          <div className="text-gray-400">Cinematic • Freestyle</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <motion.button
                    onClick={() => navigate('/fpv')}
                    className="mt-6 px-6 py-2 bg-orange-400/20 border border-orange-400/50 text-orange-400 font-mono text-sm hover:bg-orange-400/30 transition-all duration-300"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    [ENTER_TACTICAL_MODE] &gt;&gt;
                  </motion.button>
                </div>
              </motion.div>

              {/* Contact Module */}
              <motion.div
                key="contact"
                initial={{ opacity: 0, x: activeTab === 'contact' ? 0 : 50 }}
                animate={{ 
                  opacity: activeTab === 'contact' ? 1 : 0,
                  x: activeTab === 'contact' ? 0 : 50,
                  display: activeTab === 'contact' ? 'block' : 'none'
                }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className={activeTab === 'contact' ? 'block' : 'hidden'}
              >
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                  <motion.div 
                    className="text-green-400 font-mono text-sm mb-6 cyber-glow"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 0.8 }}
                    transition={{ delay: 0.2 }}
                  >
                    bash@neural-core:~$ cat /etc/network/contact_protocols.conf
                  </motion.div>

                  {/* Contact Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Professional Channels */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                      className="bg-black/40 border border-cyan-400/30 p-6 rounded"
                    >
                      <div className="text-cyan-400 font-mono text-lg mb-4">PROFESSIONAL_CHANNELS</div>
                      <div className="space-y-3 font-mono text-sm">
                        <div className="flex items-center justify-between p-3 border border-gray-600 rounded hover:border-cyan-400/50 transition-colors cursor-pointer">
                          <div>
                            <div className="text-white">Email</div>
                            <div className="text-gray-400 text-xs">muhamadbashirushdan@gmail.com</div>
                          </div>
                          <div className="text-cyan-400">[SEND]</div>
                        </div>
                        <div className="flex items-center justify-between p-3 border border-gray-600 rounded hover:border-cyan-400/50 transition-colors cursor-pointer">
                          <div>
                            <div className="text-white">LinkedIn</div>
                            <div className="text-gray-400 text-xs">Professional Network</div>
                          </div>
                          <div className="text-cyan-400">[CONNECT]</div>
                        </div>
                        <div className="flex items-center justify-between p-3 border border-gray-600 rounded hover:border-cyan-400/50 transition-colors cursor-pointer">
                          <div>
                            <div className="text-white">GitHub</div>
                            <div className="text-gray-400 text-xs">Code Repository</div>
                          </div>
                          <div className="text-cyan-400">[VIEW]</div>
                        </div>
                      </div>
                    </motion.div>

                    {/* Quick Contact Form */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                      className="bg-black/40 border border-green-400/30 p-6 rounded"
                    >
                      <div className="text-green-400 font-mono text-lg mb-4">SECURE_MESSAGE</div>
                      <div className="space-y-4">
                        <div>
                          <div className="text-white font-mono text-sm mb-2">SUBJECT:</div>
                          <input 
                            type="text" 
                            className="w-full bg-black border border-gray-600 p-2 text-white font-mono text-sm focus:border-green-400 outline-none"
                            placeholder="Enter subject..."
                          />
                        </div>
                        <div>
                          <div className="text-white font-mono text-sm mb-2">MESSAGE:</div>
                          <textarea 
                            className="w-full bg-black border border-gray-600 p-2 text-white font-mono text-sm h-24 focus:border-green-400 outline-none resize-none"
                            placeholder="Enter message..."
                          />
                        </div>
                        <motion.button
                          className="w-full bg-green-400/20 border border-green-400/50 text-green-400 font-mono text-sm py-2 hover:bg-green-400/30 transition-all duration-300"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          [TRANSMIT_MESSAGE] &gt;&gt;
                        </motion.button>
                      </div>
                    </motion.div>
                  </div>

                  {/* Status */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="mt-6 bg-black/40 border border-yellow-400/30 p-4 rounded"
                  >
                    <div className="flex items-center justify-between font-mono text-sm">
                      <div className="text-yellow-400">AVAILABILITY_STATUS:</div>
                      <div className="text-green-400">AVAILABLE FOR HIRE</div>
                    </div>
                    <div className="text-gray-400 text-xs mt-2">
                      Location: Kelantan, Malaysia • Remote work available • Response time: 24h
                    </div>
                  </motion.div>
                </div>
              </motion.div>

              {/* Holographic transition effect */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-cyan-400/20 via-transparent to-cyan-400/20 pointer-events-none"
                initial={{ x: '-100%' }}
                animate={{ x: '100%' }}
                transition={{ 
                  duration: 0.8, 
                  ease: 'linear',
                  repeat: 0
                }}
                key={activeTab}
              />
              </>
              )}
            </div>

            {/* Bottom HUD Bar */}
            <div className="flex items-center justify-between px-4 sm:px-8 py-3 border-t border-cyan-400/30 bg-gradient-to-r from-transparent via-black/40 to-transparent backdrop-blur-sm">
              <div className="flex items-center space-x-3 sm:space-x-6 text-xs font-mono">
                <div className="flex items-center space-x-2">
                  <span className="text-gray-500 hidden sm:inline">ACTIVE MODULE:</span>
                  <span className="text-gray-500 sm:hidden">MODULE:</span>
                  <span className="text-cyan-400 cyber-glow text-xs">
                    {activeTab === 'system' ? (
                      <>
                        <span className="hidden sm:inline">SYSTEM_STATUS</span>
                        <span className="sm:hidden">STATUS</span>
                      </>
                    ) : (
                      <>
                        <span className="hidden sm:inline">MODULE_NAVIGATOR</span>
                        <span className="sm:hidden">MODULES</span>
                      </>
                    )}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-1 h-1 bg-green-400 rounded-full animate-pulse"></div>
                  <span className="text-green-400 hidden sm:inline">NEURAL_LINK_ACTIVE</span>
                  <span className="text-green-400 sm:hidden">ACTIVE</span>
                </div>
              </div>
              
              <div className="flex items-center space-x-2 sm:space-x-4 text-xs font-mono text-gray-500">
                <span className="hidden sm:inline">TAB to switch</span>
                <img src="/br-logo.png" alt="BR" className="w-3 h-3 sm:w-4 sm:h-4 opacity-30" />
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default Home;